import { cva, cx } from '@vizit/theme';
import { hasScore } from '@vizit/utils';
import React from 'react';
import { type AriaButtonOptions, type PressEvent, mergeProps, useButton, useFocusWithin, useHover } from 'react-aria';
import { AssetContext as AssetContextValue } from './asset.context-provider';
import type { AssetProps } from './types';

export const styles = cva({
  base: 'relative outline-none transition-all ease-in-out',
  variants: {
    isHovered: {
      // TODO: There is currently no hover state at this level, but this is a placeholder for when we add it
      true: '',
      false: '',
    },
    isDisabled: {
      true: 'cursor-wait',
      false: '',
    },
    isViewOnly: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: {
    isViewOnly: false,
  },
});

function Asset(props: AssetProps) {
  const {
    children,
    className,
    id,
    isLoading = false,
    isSelected = false,
    isUnscored = false,
    unscoredLabel,
    isAiGenerated = false,
    isViewOnly = false,
    score,
    showScoreBar = true,
    slotNumber = null,
    onPress,
    onAssetClick,
    ...other
  } = props;

  const ref = React.useRef<HTMLDivElement>(null);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const [hasContextMenu, setHasContextMenu] = React.useState(false);
  const isScored = React.useMemo(() => hasScore(score), [score]);

  const handlePress = React.useCallback(
    (event: PressEvent) => {
      onAssetClick?.(id);
      onPress?.(event);
    },
    [id, onAssetClick, onPress],
  );

  const { hoverProps } = useHover({ onHoverChange: setIsHovered });
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setIsFocused });
  const { buttonProps } = useButton(
    {
      ...(other as AriaButtonOptions<'div'>),
      onPress: handlePress,
      elementType: 'div',
      isDisabled: isLoading,
    },
    ref,
  );

  const interactionProps = isViewOnly ? focusWithinProps : mergeProps(buttonProps, hoverProps, focusWithinProps);

  return (
    <AssetContextValue.Provider
      value={{
        hasContextMenu,
        isHovered,
        isFocused,
        isLoading,
        isSelected: isSelected || isFocused,
        isUnscored,
        unscoredLabel,
        isAiGenerated,
        isViewOnly,
        score: isScored ? score : null,
        showScoreBar,
        slotNumber,
        onContextMenuMount: setHasContextMenu,
        onHoverChange: setIsHovered,
      }}
    >
      <div
        className={cx(styles({ isHovered, isDisabled: isLoading, isViewOnly }), className)}
        {...interactionProps}
        data-focus={isFocused}
        data-hovered={isHovered}
        {...other}
      >
        {children}
      </div>
    </AssetContextValue.Provider>
  );
}

export { Asset };
